import { ReactNode, useState } from "react";
import { Collapse, InputAdornment, useTheme } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import TuneIcon from "@mui/icons-material/Tune";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import DSBox from "views/system-design/DSBox";
import DSButton from "views/system-design/DSButton";
import DSTextField from "views/system-design/DSTextField";
import DSTypography from "views/system-design/DSTypography";

export type FilterBarProps = {
  search?: {
    /** controlled value — when provided it always wins */
    value?: string;
    /** initial value for uncontrolled usage */
    defaultValue?: string;
    onChange: (value: string) => void;
    placeholder?: string;
    onSubmit?: () => void;
    /** defaults to 560 */
    maxWidth?: number;
  };
  /** controls rendered right after the search field (always visible) */
  afterSearch?: ReactNode;
  /** number of non-default filters currently applied — shown on the Filters button */
  activeCount?: number;
  /** extra controls rendered inline next to the search (always visible) */
  quickActions?: ReactNode;
  /** filter controls revealed by the Filters button */
  children?: ReactNode;
  /** start with the filter panel open */
  defaultOpen?: boolean;
};

/**
 * Compact list toolbar: a prominent search field plus a collapsible
 * "Filters" panel, instead of a permanent wall of selects.
 */
const FilterBar = ({
  search,
  afterSearch,
  activeCount = 0,
  quickActions,
  children,
  defaultOpen = false,
}: FilterBarProps) => {
  const { palette } = useTheme();
  const [open, setOpen] = useState(defaultOpen);
  const [innerSearch, setInnerSearch] = useState(search?.defaultValue ?? "");
  const searchValue = search?.value !== undefined ? search.value : innerSearch;

  return (
    <DSBox sx={{ mb: 2.5 }}>
      <DSBox
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexWrap: "wrap",
        }}
      >
        {search && (
          <DSTextField
            value={searchValue}
            onChange={(e) => {
              // inner state only drives the uncontrolled mode — skip the
              // extra render when the caller controls the value
              if (search.value === undefined) setInnerSearch(e.target.value);
              search.onChange(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") search.onSubmit?.();
            }}
            placeholder={search.placeholder ?? "Search…"}
            size="small"
            sx={{
              flex: `1 1 ${Math.min(260, search.maxWidth ?? 560)}px`,
              maxWidth: search.maxWidth ?? 560,
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    fontSize="small"
                    sx={{ color: palette.text.disabled }}
                  />
                </InputAdornment>
              ),
            }}
          />
        )}
        {afterSearch}
        {children && (
          <DSButton
            onClick={() => setOpen((o) => !o)}
            startIcon={open ? <ExpandLessIcon /> : <TuneIcon />}
            sx={{
              color:
                activeCount > 0 ? palette.primary.main : palette.text.secondary,
              backgroundColor: palette.white[5],
              border: `1px solid ${palette.white[8]}`,
              boxShadow: "none",
              px: 2.5,
              "&:hover": {
                backgroundColor: palette.white[8],
                boxShadow: "none",
              },
            }}
          >
            Filters
            {activeCount > 0 && (
              <DSBox
                sx={{
                  ml: 1,
                  minWidth: 18,
                  height: 18,
                  borderRadius: "9px",
                  px: 0.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: palette.primary.main,
                }}
              >
                <DSTypography
                  sx={{
                    fontSize: "11px",
                    fontFamily: "Gilroy-SemiBold",
                    color: palette.primary.contrastText,
                    lineHeight: 1,
                  }}
                >
                  {activeCount}
                </DSTypography>
              </DSBox>
            )}
          </DSButton>
        )}
        {quickActions}
      </DSBox>
      {children && (
        <Collapse in={open}>
          <DSBox
            sx={{
              mt: 1.5,
              p: 2,
              borderRadius: "20px",
              backgroundColor: palette.white[5],
              border: `1px solid ${palette.white[8]}`,
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 2,
            }}
          >
            {children}
          </DSBox>
        </Collapse>
      )}
    </DSBox>
  );
};

export default FilterBar;
